import React, { useEffect, useState } from "react";
import axios from "axios";
import { CircleArrowLeft, CircleArrowRight, Play } from "lucide-react";
import defaultAvatar from "../assets/profile.png";
import { useSocket } from "../contexts/SocketContext";
import { getDisplayAvatar, getDisplayName } from "./ChatItem";
import { clearChatHistoryCache } from "./chatscreen";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const MEDIA_PREVIEW_COUNT = 5;

const MediaThumb = ({ item }) =>
  item.type === "video" ? (
    <>
      <video src={item.url} preload="metadata" muted className="w-full h-full object-cover bg-black" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center">
          <Play className="w-4 h-4 ml-0.5" />
        </span>
      </span>
    </>
  ) : (
    <img src={item.url} alt="media" className="w-full h-full object-cover" />
  );

const UserProfile = ({ chat, sharedMedia = [], onBack, onChatUpdate, onChatCleared }) => {
  const { token, emitWithAck } = useSocket();
  const [profile, setProfile] = useState(null);
  const [followers, setFollowers] = useState([]);
  const [showAllMedia, setShowAllMedia] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isBlocked = chat.myStatus === "blocked";

  useEffect(() => {
    if (!chat?.id || !token) return;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/user/profile`, { params: { id: chat.id }, headers: { token } })
      .then(({ data }) => {
        if (!cancelled) setProfile(data?.result || null);
      })
      .catch(() => {});
    // A few follower pictures for the avatar stack
    axios
      .post(
        `${API_BASE_URL}/friend/get-friends`,
        { filters: { user_id_2: chat.id, status: "accepted" }, page: 1, limit: 3 },
        { headers: { token } }
      )
      .then(({ data }) => {
        if (!cancelled) setFollowers((data?.result?.friends || []).map((f) => f.User1).filter(Boolean));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chat?.id, token]);

  const name = profile ? getDisplayName(profile) : chat.name;
  const username = profile?.username || chat.username;
  const avatar = (profile && getDisplayAvatar(profile)) || chat.avatar || defaultAvatar;
  const followersCount = Number(profile?.followersCount) || 0;
  const hiddenMediaCount = sharedMedia.length - MEDIA_PREVIEW_COUNT;
  const media = showAllMedia ? sharedMedia : sharedMedia.slice(0, MEDIA_PREVIEW_COUNT);

  const handleClearChat = async () => {
    if (!window.confirm(`Clear your chat with ${name}? Messages are removed for you only.`)) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API_BASE_URL}/group_chat/delete-user-chats`, { user_id: chat.id }, { headers: { token } });
      clearChatHistoryCache(token, chat.id);
      onChatCleared(chat.id);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to clear the chat.");
      setBusy(false);
    }
  };

  const handleBlockToggle = async () => {
    const question = isBlocked ? `Unblock ${name}?` : `Block ${name}? They will not be able to message you.`;
    if (!window.confirm(question)) return;
    setBusy(true);
    setError("");
    try {
      const res = await emitWithAck("block-user", { userId: chat.id, status: isBlocked ? "unblock" : "blocked" });
      onChatUpdate(chat.id, { myStatus: res.message?.sender?.myStatus || null });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[460px] h-[70vh] overflow-y-auto">
      {/* Profile banner */}
      <div className="chat-profile-bg rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <img
          src={avatar}
          alt={name}
          className="w-[110px] h-[110px] sm:w-[130px] sm:h-[130px] rounded-3xl border-4 border-[#F7A440] bg-[#FFC94A] object-cover shrink-0"
        />
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-[#000000] truncate">{name}</h2>
          {username && <p className="text-[#4B4B4B] text-sm truncate">@{username}</p>}
          <div className="flex items-center gap-2 mt-2">
            {followers.length > 0 && (
              <div className="flex -space-x-2">
                {followers.map((f) => (
                  <img
                    key={f.id}
                    src={f.profile_picture_url || defaultAvatar}
                    alt={getDisplayName(f)}
                    title={getDisplayName(f)}
                    className="w-6 h-6 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
            )}
            <span className="text-xs italic font-semibold text-[#095228]">
              {followersCount} {followersCount === 1 ? "Follower" : "Followers"}
            </span>
          </div>
          <a
            href={`/myprofile/${chat.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 bg-white text-[#095228] text-xs font-semibold px-3 py-1.5 rounded-full border border-[#39B54A] hover:bg-[#F4FBF5]"
          >
            View Profile <CircleArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="flex justify-end mt-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white text-sm font-semibold bg-[#095228] px-4 py-2 rounded-full hover:bg-[#0b6631]"
        >
          <CircleArrowLeft className="w-4 h-4" /> Back Chat
        </button>
      </div>

      <div className="px-2 sm:px-4 flex-1">
        {/* Contact info (only what the user's privacy settings allow) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="min-w-0">
            <h4 className="font-normal text-[#797C7B] text-xs mb-1">Email Address</h4>
            <p className="font-semibold text-[#000E08] text-sm break-all">{profile?.email || "Not shared"}</p>
          </div>
          <div>
            <h4 className="font-normal text-[#797C7B] text-xs mb-1">Phone Number</h4>
            <p className="font-semibold text-[#000E08] text-sm">{profile?.mobile_number || "Not shared"}</p>
          </div>
        </div>

        {/* Media Shared */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-normal text-[#797C7B] text-xs">Media Shared</h4>
            {sharedMedia.length > MEDIA_PREVIEW_COUNT && (
              <button
                onClick={() => setShowAllMedia((v) => !v)}
                className="text-xs text-[#095228] font-bold underline"
              >
                {showAllMedia ? "Show Less" : "View All"}
              </button>
            )}
          </div>
          {sharedMedia.length === 0 ? (
            <p className="text-sm text-gray-500">No media shared yet.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {media.map((item, i) => {
                const isLastPreview = !showAllMedia && hiddenMediaCount > 0 && i === MEDIA_PREVIEW_COUNT - 1;
                return (
                  <a
                    key={`${item.url}_${i}`}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (isLastPreview) {
                        e.preventDefault();
                        setShowAllMedia(true);
                      }
                    }}
                    className="relative block aspect-square rounded-lg overflow-hidden bg-gray-100"
                  >
                    <MediaThumb item={item} />
                    {isLastPreview && (
                      <span className="absolute inset-0 bg-black/60 text-white font-semibold flex items-center justify-center">
                        {hiddenMediaCount}+
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-500 text-center mt-4">{error}</p>}

      <div className="flex gap-3 sm:gap-6 mt-6 mb-2 px-2 sm:px-4">
        <button
          onClick={handleClearChat}
          disabled={busy}
          className="flex-1 bg-[#EBF8ED] border border-[#095228] text-[#095228] text-sm sm:text-base py-2.5 rounded-full hover:bg-[#dff3e3] poppins font-semibold disabled:opacity-50"
        >
          Clear Chat
        </button>
        <button
          onClick={handleBlockToggle}
          disabled={busy}
          className="flex-1 bg-[#BE1F2E] text-white text-sm sm:text-base py-2.5 rounded-full hover:bg-red-700 poppins font-semibold disabled:opacity-50"
        >
          {isBlocked ? "Unblock Person" : "Block Person"}
        </button>
      </div>
    </div>
  );
};

export default UserProfile;
